import { Address } from "../../../features/address"
import { Data2, useDataTypesHooks, useDescribeHooks } from "../../../features/data-types"
import { defined, firstItem } from "../../../utils/listUtils"
import { addLeadingZeros, round } from "../../../utils/typeUtils"
import { Params } from "../../../state/reducers/types"
import { DataType } from "../../../tables"
import { ListResponse } from "../../common"
import { useBranchLoaders, useManagerHooks } from "../../manager"
import { ItemValidation, useOperationLoaders } from "../../operations"
import { Product, ProductType, useLotLoaders, useProductHooks, useProductLoaders } from "../../products"
import { SaleOperation, useCustomerLoaders } from "../../sales"
import { unitTypeHelpers, UnitTypeName, useStockLoaders, useUnitTypeHooks, useUnitTypeLoaders } from "../../stock"
import { DeliveryItemRow } from "../components/types"
import { useDeliveryLoaders } from "../loaders/deliveryLoaders"
import { useDeliveryOrderLoaders } from "../loaders/deliveryOrderLoaders"
import { Delivery, DeliveryMethod, DeliveryOrder, DeliveryStatus } from "../state/types"
import { useDeliveryOrderHooks } from "./deliveryOrderHooks"
import { DeliveryNoteFilters, DeliveryNoteStatusFilter } from "./types"

export const useDeliveryHooks = () => {
    const { getEquivalence } = unitTypeHelpers
    const { describeQuantity: describeProductQuantity, getWeight: getProductWeight } = useProductHooks()
    const { describeContent } = useUnitTypeHooks()
    const { getDeliveryAddress } = useDeliveryOrderHooks()
    const { describe } = useDescribeHooks()
    const { formatDate, formatNumber } = useDataTypesHooks()
    const { currentBranchId } = useManagerHooks()

    const deliveryLoaders = useDeliveryLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()
    const operationLoaders = useOperationLoaders()
    const productLoaders = useProductLoaders()
    const stockLoaders = useStockLoaders()
    const lotLoaders = useLotLoaders()
    const branchLoaders = useBranchLoaders()
    const customerLoaders = useCustomerLoaders()

    const describeDelivery = async (delivery: Delivery, deliveryOrder?: DeliveryOrder): Promise<string> => {
        const { loadOperation } = operationLoaders()
        const operation = await loadOperation(delivery.table, delivery.operationId)
        const address = await getDeliveryAddress(delivery)
        const infoLabel = `${formatDate(new Date(delivery.date))} - ${address.locality}`
        return `${deliveryHelpers.describeDeliveryNote(delivery, deliveryOrder)} - ${describe(delivery.table, operation!)} - ${infoLabel}`
    }

    const describeQuantity = (
        quantity: number,
        product: Product
    ) => {
        const isBulk = product.type === ProductType.BULK
        return isBulk ? describeProductQuantity(quantity, product) : formatNumber(quantity)
    }

    const describeWeight = async (delivery: Delivery) => {
        const weight = await getWeight(delivery)
        return describeContent(weight.units, weight.unitTypeId)
    }

    const getDeliveryAddresses = async (operation: SaleOperation) => {
        const { loadOperationDeliveries } = deliveryLoaders()
        const deliveries = await loadOperationDeliveries(operation.id)

        const { findBranch } = branchLoaders()
        const branch = findBranch(operation.branchId)!
        const branchAddress = branch.address

        const { loadCustomer } = customerLoaders()
        const customer = await loadCustomer(operation.customerId)
        const customerAddresses = deliveries
            .filter(delivery => delivery.method === DeliveryMethod.SHIPMENT)
            .map(delivery => customer!.deliveryAddresses.find(address => address.id === delivery.addressId)!)

        const addresses: Address[] = []
        if (firstItem(deliveries)?.method === DeliveryMethod.SHIPMENT) {
            addresses.push(...customerAddresses)
        }
        if (firstItem(deliveries)?.method === DeliveryMethod.PICKUP) {
            addresses.push(branchAddress)
        }

        return addresses
    }

    const getWeight = async (delivery: Delivery) => {
        const { findUnitTypeByName, findUnitType }  = unitTypeLoaders()
        const weightUnitType = findUnitTypeByName(UnitTypeName.KILOGRAM)!
        
        const { loadOperation } = operationLoaders()
        const operation = await loadOperation(delivery.table, delivery.operationId)
        const productIds = operation!.items.map(item => item.productId) || []
        const { loadProducts } = productLoaders()
        const products = await loadProducts(productIds)
        
        const units = delivery.items.reduce((acc, deliveryItem) => {
            const item = operation!.items.find(item => item.id === deliveryItem.itemId)!
            const product = products.find(product => product.id === item.productId)!
            const weight = getProductWeight(product)
            const unitType = findUnitType(weight.unitTypeId)!
            const equivalence = getEquivalence(unitType, weightUnitType)!
            return round(acc + (weight.units * equivalence * deliveryItem.quantity))
        }, 0)

        return {
            units,
            unitTypeId: weightUnitType.id
        }
    }

    // The code is stored with leading zeros ("012-003"): "12-3" or "0012-003" are normalized to match it
    const normalizeDeliveryNoteCode = (text: string): string => {
        const match = text.match(/^(\d+)\s*-\s*(\d+)$/)
        return match ? `${addLeadingZeros(Number(match[1]))}-${addLeadingZeros(Number(match[2]))}` : text
    }

    // TODO: without an index on Deliveries2 the batch can't be sorted by date in Dynamo (pending index by branchId + date)
    const loadDeliveryNotesBatch = async (
        filtersData: Data2 = {},
        batchSize?: number,
        startKey?: Params
    ): Promise<ListResponse<Delivery>> => {
        const { searchText, method, table, customerId, status, dateFrom, dateTo } = filtersData as DeliveryNoteFilters

        const textFilters = searchText?.trim() ?
            [{ prop: 'codeStr', text: normalizeDeliveryNoteCode(searchText.trim()) }] :
            undefined

        const valueFilters: { prop: string, value: string | boolean, equals?: boolean }[] = [
            { prop: 'branchId', value: currentBranchId! }
        ]
        if (method) {
            valueFilters.push({ prop: 'method', value: method })
        } else {
            valueFilters.push({ prop: 'method', value: DeliveryMethod.RECEPTION, equals: false })
        }
        table && valueFilters.push({ prop: 'table', value: table })
        customerId && valueFilters.push({ prop: 'customerId', value: customerId })
        if (status === DeliveryNoteStatusFilter.CANCELLED) {
            valueFilters.push({ prop: 'cancelled', value: true })
        } else if (status) {
            valueFilters.push({ prop: 'deliveryStatus', value: status === DeliveryNoteStatusFilter.DELIVERED ? DeliveryStatus.DELIVERED : DeliveryStatus.PENDING })
            valueFilters.push({ prop: 'cancelled', value: true, equals: false })
        }

        const dateTimeFrom = dateFrom
        dateTimeFrom?.setHours(0, 0, 0, 0)
        const dateTimeTo = dateTo
        dateTimeTo?.setHours(23, 59, 59, 999)
        const rangeFilters = dateTimeFrom || dateTimeTo ?
            [{
                prop: 'date',
                ...(dateTimeFrom && { start: dateTimeFrom.toISOString() }),
                ...(dateTimeTo && { end: dateTimeTo.toISOString() })
            }] :
            undefined

        const response = await deliveryLoaders().loadDeliveriesBatch({
            startKey,
            limit: batchSize,
            textFilters,
            valueFilters,
            rangeFilters
        }, false)

        return response as ListResponse<Delivery>
    }

    const loadDeliveryNotesBatchData = async (deliveriesDT: DataType[]) => {
        const deliveries = deliveriesDT as Delivery[]
        const { loadDeliveryOrders } = deliveryOrderLoaders()
        await loadDeliveryOrders(defined(deliveries.map(delivery => delivery.orderId)))
        const { loadDeliveriesOperations } = operationLoaders()
        await loadDeliveriesOperations(deliveries)
        const { loadCustomers } = customerLoaders()
        await loadCustomers(defined(deliveries.map(delivery => delivery.customerId)))
    }

    const refreshDeliveries = (deliveries: Delivery[]) => {
        const { loadDeliveries } = deliveryLoaders()
        return loadDeliveries(deliveries.map(delivery => delivery.id), true)
    }

    const validateCompleteDeliveryItems = async (
        items: DeliveryItemRow[],
        itemValidations: ItemValidation[],
        branchId: string
    ): Promise<string[]> => {
        const validations: string[] = []
        const validateDefinedLot = itemValidations.includes(ItemValidation.DEFINED_LOT)
        const validateAvailableStock = itemValidations.includes(ItemValidation.AVAILABLE_STOCK)
        const validateReservedStock = itemValidations.includes(ItemValidation.RESERVED_STOCK)

        await items.reduce(async (previousPromise, item) => {
            await previousPromise

            if (item.quantity === 0) return

            const { loadProduct } = productLoaders()
            const product = (await loadProduct(item.productId!))!

            if (validateDefinedLot && product.loted && !item.lotId && !item.lotData?.lotCode) {
                validations.push(`Debe seleccionar una partida para el producto ${product.name}`)
            } else if (validateAvailableStock || validateReservedStock) {
                const { loadProductStocks } = stockLoaders()
                const productStocks = await loadProductStocks(product.id, branchId, item.lotId)
                const availableUnits = productStocks.reduce((acc, stock) => acc + stock.availableUnits, 0)
                const reservedUnits = productStocks.reduce((acc, stock) => acc + stock.reservedUnits, 0)
                // Con ventas sin stock el disponible queda negativo: solo se puede entregar lo reservado que existe físicamente
                const physicalReservedUnits = Math.min(reservedUnits, Math.max(availableUnits + reservedUnits, 0))
                const units = validateAvailableStock ? availableUnits : physicalReservedUnits

                const { loadLot } = lotLoaders()
                const lot = item.lotId ? await loadLot(item.lotId) : undefined
                const productLabel = `el producto ${product.name}${lot ? ' (' + lot.code + ')' : ''}`

                const missingPhysicalStock = validateReservedStock && reservedUnits >= item.quantity && units < item.quantity
                if (missingPhysicalStock) {
                    validations.push(units === 0 ?
                        `No hay stock físico para entregar ${productLabel}: se vendió sin stock y todavía no ingresó mercadería` :
                        `Solo hay ${units} unidades físicas para entregar ${productLabel}: el resto se vendió sin stock y todavía no ingresó mercadería`
                    )
                } else if (units < item.quantity) {
                    validations.push(units === 0 ?
                        `Debe eliminar o modificar la partida d${productLabel} porque no contiene stock${validateReservedStock ? ' reservado' : ''}` :
                        `Debe ingresar una cantidad menor o igual al stock${validateReservedStock ? ' reservado' : ''} (${units}) para ${productLabel}`
                    )
                }
            }
        }, Promise.resolve())

        return validations
    }

    // Advertencias al emitir una orden de entrega por más de lo que hay físicamente (ej. productos vendidos sin stock).
    // No bloquean: la orden se puede emitir, pero la entrega no se va a poder concretar hasta que ingrese mercadería.
    const getPhysicalStockWarnings = async (
        items: DeliveryItemRow[],
        branchId: string
    ): Promise<string[]> => {
        const warnings: string[] = []
        const { loadProduct } = productLoaders()
        const { loadProductStocks } = stockLoaders()

        await items.reduce(async (previousPromise, item) => {
            await previousPromise
            if (!item.quantity) return

            const product = (await loadProduct(item.productId))!
            const productStocks = await loadProductStocks(product.id, branchId, item.lotId)
            const availableUnits = productStocks.reduce((acc, stock) => acc + stock.availableUnits, 0)
            const reservedUnits = productStocks.reduce((acc, stock) => acc + stock.reservedUnits, 0)
            const physicalReservedUnits = Math.min(reservedUnits, Math.max(availableUnits + reservedUnits, 0))

            if (physicalReservedUnits < item.quantity) {
                warnings.push(`${product.name}: se quieren entregar ${describeProductQuantity(item.quantity, product)} y hay ${describeProductQuantity(physicalReservedUnits, product)} en depósito`)
            }
        }, Promise.resolve())

        return warnings
    }

    return {
        getPhysicalStockWarnings,
        describeDelivery,
        describeQuantity,
        describeWeight,
        getDeliveryAddress,
        getDeliveryAddresses,
        getWeight,
        loadDeliveryNotesBatch,
        loadDeliveryNotesBatchData,
        refreshDeliveries,
        validateCompleteDeliveryItems
    }
}

export const deliveryHelpers = {
    // Default delivery date when emitting an order: the operation one only if it isn't already past, otherwise today
    getDefaultDeliveryDate: (operationDeliveryDate?: string): string => {
        const now = new Date()
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const isValid = !!operationDeliveryDate && new Date(operationDeliveryDate) >= startOfToday
        return isValid ? operationDeliveryDate! : now.toISOString()
    },

    // Every effective delivery (shipment, pickup or reception) is a "Remito". Returns are documented as a "Nota de Devolución".
    getDeliveryNoteLabel: (delivery?: Delivery): string => {
        return delivery?.method === DeliveryMethod.RETURN ? 'Nota de Devolución' : 'Remito'
    },

    describeDelivery: (delivery: Delivery, deliveryOrder?: DeliveryOrder, _deliveryNoteLabel = false): string => {
        return deliveryHelpers.describeDeliveryNote(delivery, deliveryOrder)
    },

    describeDeliveryCode: (delivery?: Delivery, deliveryOrder?: DeliveryOrder): string => {
        return `${deliveryOrder ? addLeadingZeros(deliveryOrder.code) + '-' : ''}${addLeadingZeros(delivery?.code || 1)}`
    },

    describeDeliveryNote: (delivery: Delivery, deliveryOrder?: DeliveryOrder) => {
        return `${deliveryHelpers.getDeliveryNoteLabel(delivery)} ${deliveryHelpers.describeDeliveryCode(delivery, deliveryOrder)}`
    },

    getCreateDeliveryLabel: (deliveryMethod: DeliveryMethod): string => {
        return new Map<DeliveryMethod, string>()
            .set(DeliveryMethod.RECEPTION, 'Completar Recepción')
            .set(DeliveryMethod.PICKUP, 'Completar Retiro')
            .set(DeliveryMethod.SHIPMENT, 'Generar Remito')
            .set(DeliveryMethod.RETURN, 'Completar Devolución')
            .get(deliveryMethod)!
    },

    getCreateDeliveryIcon: (deliveryMethod: DeliveryMethod): string => {
        return new Map<DeliveryMethod, string>()
            .set(DeliveryMethod.RECEPTION, 'cart-check-fill')
            .set(DeliveryMethod.PICKUP, 'bag-check-fill')
            .set(DeliveryMethod.SHIPMENT, 'file-spreadsheet')
            .set(DeliveryMethod.RETURN, 'boxes')
            .get(deliveryMethod)!
    },

    // Only an effective (not annulled) shipment or pickup with delivered items can originate a return
    isReturnable: (delivery: Delivery, deliveryOrder?: DeliveryOrder): boolean => {
        const isDeliveryNote = [DeliveryMethod.SHIPMENT, DeliveryMethod.PICKUP].includes(delivery.method)
        const hasDeliveredItems = delivery.items.some(item => item.deliveredQuantity > 0)
        const hasOrderDeliveredItems = !!deliveryOrder?.items.some(item => item.deliveryStats.delivered > 0)
        return isDeliveryNote && hasDeliveredItems && hasOrderDeliveredItems && !delivery.cancelled
    },

    isCompletable: (delivery: Delivery, deliveryOrder?: DeliveryOrder): boolean => {
        const isShipment = delivery.method === DeliveryMethod.SHIPMENT
        return isShipment && delivery.deliveryStatus !== DeliveryStatus.DELIVERED && !delivery.cancelled && !deliveryOrder?.cancelled
    },

    isAnnulable: (delivery: Delivery, deliveryOrder?: DeliveryOrder): boolean => {
        const isShipment = delivery.method === DeliveryMethod.SHIPMENT
        const hasPendingItems = delivery.items.some(item => (item.deliveredQuantity + item.notDeliveredQuantity) === 0)
        return isShipment && hasPendingItems && !delivery.cancelled && !deliveryOrder?.cancelled
    }
}
